import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller } from 'react-hook-form'
import { Autocomplete, List, ListSubheader, MenuItem, TextField } from '@mui/material'

const BenchmarkAutocomplete = ({
  name,
  control,
  defaultValue,
  freeSolo,
  disabled,
  selectedBenchmark,
  setSelectedBenchmark,
  benchmarksData,
  isLoadingBenchmarksData,
  materialTypeData,
  tradePrice,
  isPricelist,
  InputProps,
  isBuy,
  ...restProps
}) => {
  const [options, setOptions] = useState([])

  useEffect(() => {
    if (benchmarksData?.Items) {
      const { defaultPriceEntry } = materialTypeData || {}
      const { sellPrice, buyPrice } = defaultPriceEntry || {}
      const referenceList = [...benchmarksData.Items]
      if (isPricelist) {
        if (sellPrice) referenceList.unshift({ ...sellPrice, name: 'Default Sell' })
        if (buyPrice) referenceList.unshift({ ...buyPrice, name: 'Default Buy' })
      } else if (isBuy && sellPrice) {
        referenceList.unshift({ ...sellPrice, name: 'Default Sell' })
      }
      setOptions(referenceList)
    }
  }, [materialTypeData, benchmarksData])

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Autocomplete
          {...field}
          value={selectedBenchmark}
          defaultValue={defaultValue}
          freeSolo={freeSolo}
          disabled={disabled}
          size="small"
          variant="outlined"
          sx={{ width: '100%' }}
          options={options}
          loading={isLoadingBenchmarksData}
          getOptionLabel={option => option?.name || ''}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          renderInput={params => <TextField {...params} InputProps={{ ...params.InputProps, ...InputProps }} />}
          renderOption={(props, option) => (
            <li {...props} key={option?.id} style={{ width: '100%' }}>
              {option?.name}
            </li>
          )}
          onChange={(_, value) => {
            setSelectedBenchmark(value)
            field.onChange(value)
          }}
          groupBy={option => (option?.history ? 'Benchmark' : 'Defaults')}
          renderGroup={params => (
            <>
              <ListSubheader disableGutters sx={{ padding: '0 10px' }}>
                {params?.group}
              </ListSubheader>
              <List>
                {params?.children?.map(child => (
                  <MenuItem disableGutters key={child.key} sx={{ padding: 0, width: '100%' }}>
                    {child}
                  </MenuItem>
                ))}
              </List>
            </>
          )}
          {...restProps}
        />
      )}
    />
  )
}

BenchmarkAutocomplete.propTypes = {
  name: PropTypes.string.isRequired,
  variant: PropTypes.string,
  defaultValue: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    publication: PropTypes.string,
  }),
  freeSolo: PropTypes.bool,
  control: PropTypes.instanceOf(Object).isRequired,
  disabled: PropTypes.bool,
  selectedBenchmark: PropTypes.object.isRequired,
  setSelectedBenchmark: PropTypes.func.isRequired,
  benchmarksData: PropTypes.object,
  isLoadingBenchmarksData: PropTypes.bool,
  materialTypeData: PropTypes.object,
  isPricelist: PropTypes.bool,
  InputProps: PropTypes.object,
  isBuy: PropTypes.bool,
  tradePrice: PropTypes.object,
}

BenchmarkAutocomplete.defaultProps = {
  variant: 'standard',
  defaultValue: null,
  freeSolo: true,
  disabled: false,
  benchmarksData: null,
  isLoadingBenchmarksData: false,
  materialTypeData: null,
  isPricelist: false,
  InputProps: {},
  isBuy: false,
  tradePrice: {},
}

export default BenchmarkAutocomplete
